import { ChevronRight } from 'lucide-react'
import {
  type ComponentType,
  type CSSProperties,
  type ReactNode,
  useId,
} from 'react'
import './fact-row.css'

type IconType = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>

/**
 * The figures a record is read for, side by side in one band.
 *
 * One fact may `lead` — the number the page exists to state, which takes the
 * weight rather than a size of its own, so the band stays one line of figures
 * read across. One leads or none does: two of them is the band ranking its own
 * numbers, which is the question this shape exists to settle.
 *
 * It is not an `InfoStrip`: nothing here opens anything. The facts are read,
 * and the record's settings are one entry point away in the page actions.
 *
 * Each fact carries its label above it, because a bare "15" or "51" names
 * nothing. Four across on a desk, two on a phone — on the row's own measure.
 */
export function FactRow({
  facts,
  values = 'figures',
  fold,
  children,
}: {
  facts: readonly {
    label: string
    /** A figure, formatted where it is read — or words, under `values="text"`. */
    value: ReactNode
    /** The one this band is read for. At most one. */
    lead?: boolean
    /** What kind of fact it is, beside its label. Optional: a label already
     *  names the fact, and the icon only lets a band be scanned by shape. */
    icon?: IconType
  }[]
  /**
   * What the values are. Figures are set as figures — the numeric face, a step
   * up — because they are compared across the band. Text is a record's own
   * facts in words (a paper, a date, what it draws on), set in the interface
   * face at body size, since a word in a figure's face reads as a code.
   */
  values?: 'figures' | 'text'
  /**
   * The working the figures come from, folded away behind a control at the
   * band's trailing edge — a drawing of the same money, a breakdown.
   *
   * The band owns the control rather than taking one, because what it needs
   * is a caret that names what it opens: a glyph alone at the end of a row of
   * figures reads as belonging to the last of them, and a screen assembling
   * one would be bespoke chrome in a screen.
   */
  fold?: {
    /** What is behind it — "By day". Shown beside the caret. */
    label: string
    open: boolean
    onToggle: () => void
  }
  /** What the fold opens onto, rendered under the band while it is open. */
  children?: ReactNode
}) {
  const id = useId()
  if (facts.length === 0) return null
  return (
    <>
      <div className="fact-row">
        <dl
          className="fact-row__facts"
          data-values={values}
          /* How many facts there are decides the columns, and is a fact about
             this row rather than about every row. */
          style={{ '--facts': facts.length } as CSSProperties}
        >
          {facts.map((fact) => (
            <div
              key={fact.label}
              className="fact-row__fact"
              data-lead={fact.lead || undefined}
            >
              <dt className="fact-row__label">
                {fact.icon ? <fact.icon size={13} aria-hidden /> : null}
                {fact.label}
              </dt>
              <dd
                className={`fact-row__value${values === 'figures' ? ' tabular' : ''}`}
              >
                {fact.value}
              </dd>
            </div>
          ))}
        </dl>
        {fold ? (
          <div className="fact-row__fold">
            <button
              type="button"
              className="fact-row__opener"
              aria-expanded={fold.open}
              aria-controls={id}
              onClick={fold.onToggle}
            >
              <ChevronRight
                size={13}
                className="fact-row__caret"
                aria-hidden="true"
              />
              {fold.label}
            </button>
          </div>
        ) : null}
      </div>
      {fold?.open ? <div id={id}>{children}</div> : null}
    </>
  )
}
